/**
 * StudyConsentsPanel - Pestana "Consentimientos" dentro del estudio.
 *
 * Permite al equipo CRO:
 *  - Importar plantillas CI del catalogo (filtradas por la categoria
 *    del estudio si esta presente, o todas si no).
 *  - Configurar para cada CI instanciado: que secciones activar
 *    (Imagenes solo si study.has_images), titulo personalizado, datos
 *    del IP autorrellenados desde staff.investigador_principal.
 *  - Generar vista previa PDF.
 *  - Enviar a un voluntario a firmar via VIDsigner (doble firma:
 *    voluntario en tableta + Investigador Principal por portal Validated ID).
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { usePermissions } from '../../contexts/PermissionsContext';
import {
  Plus, DownloadSimple, Trash, PaperPlaneTilt, Pencil, FloppyDisk, XCircle,
  ChartBar, CaretDown, CaretUp, ArrowsClockwise, FilePdf, UploadSimple, Envelope, Eye,
} from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Card, CardContent } from '../ui/card';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { errText } from '../../utils/errors';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABELS = {
  producto_sanitario: 'Producto Sanitario',
  medicamento: 'Medicamento',
  cosmetico: 'Cosmetico',
  suplemento: 'Suplemento Alimenticio',
};

export default function StudyConsentsPanel({ studyId, studyHasImages, lang, highlightVolunteerId }) {
  // URS-F359: director_estudio puro EN ESTE ESTUDIO no debe ver nombres de
  // voluntarios. Consultamos al backend (misma logica F321) en vez de mirar
  // los roles globales del usuario.
  const [isPureDirector, setIsPureDirector] = useState(false);
  useEffect(() => {
    if (!studyId) return;
    axios.get(`${API_URL}/api/studies/${studyId}/my-pii-access`, { withCredentials: true })
      .then(r => setIsPureDirector(!!r.data?.is_pure_director))
      .catch(() => setIsPureDirector(false));
  }, [studyId]);
  const [instances, setInstances] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showImport, setShowImport] = useState(false);
  const [editing, setEditing] = useState(null); // study_consent instance object
  const [sending, setSending] = useState(null); // study_consent instance
  // URS-F427: gate de permisos por estudio. Solo con `consentimientos.edit` en
  // este estudio (o admin) se ven los botones Editar / Eliminar / Añadir /
  // Enviar / Enviar HIP. Antes cualquier autenticado (ej. monitor con view)
  // podia clickear editar y actualizar el consentimiento.
  const { canStudy, isAdmin } = usePermissions();
  const canEdit = isAdmin || canStudy(studyId, 'consentimientos', 'edit');
  const canDelete = isAdmin || canStudy(studyId, 'consentimientos', 'delete');
  const [expandedTracking, setExpandedTracking] = useState({}); // {study_consent_id: bool}

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: inst }, { data: tpls }, { data: vols }] = await Promise.all([
        axios.get(`${API_URL}/api/studies/${studyId}/consents`, { withCredentials: true }),
        axios.get(`${API_URL}/api/study-consent-templates`, { withCredentials: true }),
        axios.get(`${API_URL}/api/studies/${studyId}/volunteers`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setInstances(inst || []);
      setTemplates(tpls || []);
      // URS-F415: incluye tambien voluntarios en estados terminales
      // (excluido, abandono, retirado) para que sus consentimientos
      // firmados sigan visibles en el seguimiento y no desaparezcan al
      // cambiar el estado del voluntario en el estudio (ALCOA+).
      const rawVols = Array.isArray(vols) ? vols : (vols?.items || []);
      const ALLOWED = ['incluido', 'completado', 'excluido', 'abandono', 'retirado'];
      setVolunteers(rawVols.filter(v => ALLOWED.includes(v.status)));
    } catch {
      toast.error(lang === 'es' ? 'Error al cargar consentimientos' : 'Error loading consents');
    } finally {
      setLoading(false);
    }
  }, [studyId, lang]);

  useEffect(() => { reload(); }, [reload]);

  // URS-F469d: al abrir desde la alerta "Consentimientos pendientes" del
  // Study Dashboard llega ?highlight_volunteer=VOL-XXXX. Auto-desplegamos
  // el Seguimiento de todas las CI activas para que el usuario vea de un
  // vistazo en cual falta la firma. La fila del voluntario resaltada se
  // maneja mas abajo en el sub-panel de tracking (ring ambar).
  useEffect(() => {
    if (!highlightVolunteerId || !instances.length) return;
    setExpandedTracking(prev => {
      const next = { ...prev };
      for (const inst of instances) {
        if (inst.active !== false) next[inst.study_consent_id] = true;
      }
      return next;
    });
  }, [highlightVolunteerId, instances]);

  const handleDelete = async (inst) => {
    const msg = lang === 'es'
      ? `Eliminar el consentimiento "${inst.title}" de este estudio?`
      : `Delete consent "${inst.title}" from this study?`;
    if (!window.confirm(msg)) return;
    try {
      await axios.delete(`${API_URL}/api/study-consents/${inst.study_consent_id}`, { withCredentials: true });
      toast.success(lang === 'es' ? 'Consentimiento eliminado' : 'Consent deleted');
      reload();
    } catch {
      toast.error(lang === 'es' ? 'Error al eliminar' : 'Error deleting');
    }
  };

  const handlePreview = async (inst, volunteerId) => {
    try {
      const q = volunteerId ? `?volunteer_id=${encodeURIComponent(volunteerId)}` : '';
      const resp = await axios.post(
        `${API_URL}/api/study-consents/${inst.study_consent_id}/preview${q}`,
        {},
        { withCredentials: true, responseType: 'blob' },
      );
      const url = URL.createObjectURL(resp.data);
      window.open(url, '_blank', 'noopener');
    } catch {
      toast.error(lang === 'es' ? 'Error en vista previa' : 'Preview error');
    }
  };

  return (
    <div className="space-y-4" data-testid="study-consents-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-stone-900">
            {lang === 'es' ? 'Consentimientos de este estudio' : 'Study consents'}
          </h2>
          <p className="text-sm text-stone-500">
            {lang === 'es'
              ? 'Importa una plantilla del catalogo y persoanaliza los datos antes de enviarla a firmar.'
              : 'Import a template from the catalog and customise before sending to sign.'}
          </p>
        </div>
        <Button onClick={() => setShowImport(true)} className="gap-2" data-testid="import-consent-template-btn" disabled={!canEdit} title={!canEdit ? (lang === 'es' ? 'Sin permiso para editar consentimientos' : 'No permission to edit consents') : undefined}>
          <Plus size={16} />
          {lang === 'es' ? 'Importar plantilla' : 'Import template'}
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-stone-500">{lang === 'es' ? 'Cargando...' : 'Loading...'}</p>
      ) : instances.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-stone-500">
          {lang === 'es'
            ? 'Aun no hay consentimientos en este estudio. Importa una plantilla para empezar.'
            : 'No consents yet. Import a template to start.'}
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {instances.map(inst => (
            <Card key={inst.study_consent_id} className="border-stone-200">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="space-y-1 flex-1 min-w-0">
                    <p className="font-medium text-stone-900">{inst.title}</p>
                    <div className="flex items-center gap-2 flex-wrap text-xs text-stone-500">
                      <Badge variant="outline" className="text-xs">{CATEGORY_LABELS[inst.category] || inst.category}</Badge>
                      {inst.version && (
                        <Badge variant="outline" className="text-xs bg-stone-50 text-stone-700 border-stone-200 font-mono" data-testid={`consent-inst-version-${inst.study_consent_id}`}>
                          v{inst.version}
                        </Badge>
                      )}
                      {inst.date && (
                        <Badge variant="outline" className="text-xs bg-stone-50 text-stone-700 border-stone-200 font-mono" data-testid={`consent-inst-date-${inst.study_consent_id}`}>
                          {(() => {
                            try { return new Date(inst.date + 'T00:00:00').toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
                            catch { return inst.date; }
                          })()}
                        </Badge>
                      )}
                      <span>{inst.enabled_sections?.length || 0} {lang === 'es' ? 'secciones activas' : 'sections enabled'}</span>
                      {inst.values?.investigator_name && (
                        <>
                          <span>·</span>
                          <span>{lang === 'es' ? 'IP:' : 'PI:'} <b className="text-stone-700">{inst.values.investigator_name}</b></span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="outline" size="sm" onClick={() => handlePreview(inst)} className="gap-1.5" data-testid={`consent-preview-${inst.study_consent_id}`}>
                      <DownloadSimple size={14} />
                      {lang === 'es' ? 'Vista previa' : 'Preview'}
                    </Button>
                    {canEdit && (
                      <Button variant="outline" size="sm" onClick={() => setEditing(inst)} className="gap-1.5" data-testid={`consent-edit-${inst.study_consent_id}`}>
                        <Pencil size={14} />
                        {lang === 'es' ? 'Configurar' : 'Configure'}
                      </Button>
                    )}
                    {canEdit && !isPureDirector && (
                      <Button size="sm" onClick={() => setSending(inst)} className="gap-1.5" data-testid={`consent-send-${inst.study_consent_id}`}>
                        <PaperPlaneTilt size={14} />
                        {lang === 'es' ? 'Enviar a firmar' : 'Send to sign'}
                      </Button>
                    )}
                    {!isPureDirector && (
                      <Button variant="outline" size="sm" onClick={() => setExpandedTracking(e => ({ ...e, [inst.study_consent_id]: !e[inst.study_consent_id] }))} className="gap-1.5" data-testid={`consent-tracking-toggle-${inst.study_consent_id}`}>
                        <ChartBar size={14} />
                        {lang === 'es' ? 'Seguimiento' : 'Tracking'}
                        {expandedTracking[inst.study_consent_id] ? <CaretUp size={12} /> : <CaretDown size={12} />}
                      </Button>
                    )}
                    {canDelete && (
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(inst)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`consent-delete-${inst.study_consent_id}`}>
                        <Trash size={14} />
                      </Button>
                    )}
                  </div>
                </div>
                {expandedTracking[inst.study_consent_id] && (
                  <ConsentTrackingPanel studyConsentId={inst.study_consent_id} studyId={studyId} lang={lang} highlightVolunteerId={highlightVolunteerId} />
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* URS-F494g: reordenado -- Hojas Informativas ahora aparecen
          entre Consentimientos y Otros Documentos (antes iban primero). */}
      <InfoSheetSection studyId={studyId} lang={lang} />

      {/* URS-F414: Otros Documentos (PDFs adjuntos al estudio) */}
      <OtherDocumentsSection studyId={studyId} lang={lang} />

      <ImportTemplateDialog
        open={showImport}
        onClose={() => setShowImport(false)}
        onImported={() => { setShowImport(false); reload(); }}
        studyId={studyId}
        templates={templates}
        lang={lang}
      />

      <ConfigureInstanceDialog
        open={!!editing}
        instance={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); reload(); }}
        studyHasImages={studyHasImages}
        lang={lang}
      />

      <SendToVolunteerDialog
        open={!!sending}
        instance={sending}
        onClose={() => setSending(null)}
        onSent={() => { setSending(null); reload(); }}
        volunteers={volunteers}
        lang={lang}
      />
    </div>
  );
}

/* --- Dialog: Import template ----------------------------------------- */
function ImportTemplateDialog({ open, onClose, onImported, studyId, templates, lang }) {
  const [templateId, setTemplateId] = useState('');
  const [title, setTitle] = useState('');
  // URS-F494h: version + fecha obligatorias (cabecera del PDF firmado).
  const [version, setVersion] = useState('');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setTemplateId(''); setTitle('');
      setVersion(''); setDate(new Date().toISOString().slice(0, 10));
    }
  }, [open]);

  const canSubmit = templateId && version.trim() && date && !saving;

  const handleImport = async () => {
    if (!templateId) return;
    if (!version.trim()) {
      toast.error(lang === 'es' ? 'La version es obligatoria' : 'Version is required');
      return;
    }
    if (!date) {
      toast.error(lang === 'es' ? 'La fecha es obligatoria' : 'Date is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        template_id: templateId,
        version: version.trim(),
        date,
      };
      if (title.trim()) payload.title = title.trim();
      await axios.post(`${API_URL}/api/studies/${studyId}/consents`, payload, { withCredentials: true });
      toast.success(lang === 'es' ? 'Plantilla importada' : 'Template imported');
      onImported();
    } catch (e) {
      toast.error(errText(e, lang === 'es' ? 'Error al importar' : 'Import error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{lang === 'es' ? 'Importar plantilla CI' : 'Import CI template'}</DialogTitle>
          <DialogDescription>
            {lang === 'es'
              ? 'Selecciona una plantilla del catalogo. Los datos del Investigador Principal y del estudio se autorrellenaran.'
              : 'Select a template from the catalog. PI and study fields are auto-filled.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">{lang === 'es' ? 'Plantilla del catalogo' : 'Catalog template'}</Label>
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger data-testid="import-template-select"><SelectValue placeholder={lang === 'es' ? 'Selecciona una plantilla' : 'Select a template'} /></SelectTrigger>
              <SelectContent>
                {templates.map(t => (
                  <SelectItem key={t.template_id} value={t.template_id}>
                    [{CATEGORY_LABELS[t.category] || t.category}] {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{lang === 'es' ? 'Titulo personalizado (opcional)' : 'Custom title (optional)'}</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder={lang === 'es' ? 'Ej. CI Estudio BtH Body 2.2%' : 'e.g. CI BtH Body 2.2% study'} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">
                {lang === 'es' ? 'Version' : 'Version'} <span className="text-red-500">*</span>
              </Label>
              <Input
                value={version}
                onChange={e => setVersion(e.target.value)}
                placeholder={lang === 'es' ? 'Ej. 1.0' : 'e.g. 1.0'}
                data-testid="import-template-version"
                required
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">
                {lang === 'es' ? 'Fecha' : 'Date'} <span className="text-red-500">*</span>
              </Label>
              <Input
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                data-testid="import-template-date"
                required
              />
            </div>
          </div>
          <p className="text-xs text-stone-500">
            {lang === 'es'
              ? 'Version y Fecha aparecen en la cabecera del PDF que firman voluntarios e IP (trazabilidad ICH GCP E6(R2) 4.8.10). El IP se autorrellenara desde el personal asignado del estudio.'
              : 'Version and Date will appear in the header of the PDF signed by volunteers and PI (ICH GCP E6(R2) 4.8.10 traceability). PI is auto-filled from study staff.'}
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            <XCircle size={14} className="mr-1.5" />
            {lang === 'es' ? 'Cancelar' : 'Cancel'}
          </Button>
          <Button onClick={handleImport} disabled={!canSubmit} data-testid="import-template-confirm-btn">
            <FloppyDisk size={14} className="mr-1.5" />
            {saving ? '...' : (lang === 'es' ? 'Importar' : 'Import')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* --- Dialog: Configure an existing instance --------------------------- */
function ConfigureInstanceDialog({ open, instance, onClose, onSaved, studyHasImages, lang }) {
  const [title, setTitle] = useState('');
  const [values, setValues] = useState({});
  const [enabled, setEnabled] = useState([]);
  // URS-F290: edicion del snapshot local (secciones, textos) sin afectar al template global
  const [editedSections, setEditedSections] = useState(null);
  const [snapshotDirty, setSnapshotDirty] = useState(false);
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [saving, setSaving] = useState(false);
  // URS-F494h: version + fecha (obligatorias)
  const [version, setVersion] = useState('');
  const [date, setDate] = useState('');

  useEffect(() => {
    if (instance) {
      setTitle(instance.title || '');
      setValues(instance.values || {});
      setEnabled(instance.enabled_sections || []);
      setEditedSections(((instance.template_snapshot || {}).sections || []).map(s => ({ ...s })));
      setSnapshotDirty(false);
      setEditingSectionId(null);
      setVersion(instance.version || '');
      setDate(instance.date || '');
    }
  }, [instance]);

  if (!instance) return null;
  const template = instance.template_snapshot || {};
  const sections = editedSections || template.sections || [];

  const updateSection = (sid, patch) => {
    setEditedSections(prev => (prev || []).map(s => s.id === sid ? { ...s, ...patch } : s));
    setSnapshotDirty(true);
  };
  const updateClause = (sid, cid, patch) => {
    setEditedSections(prev => (prev || []).map(s => {
      if (s.id !== sid) return s;
      const clauses = (s.clauses || []).map(c => c.id === cid ? { ...c, ...patch } : c);
      return { ...s, clauses };
    }));
    setSnapshotDirty(true);
  };

  const toggleSection = (sid, checked) => {
    setEnabled(prev => checked ? Array.from(new Set([...prev, sid])) : prev.filter(x => x !== sid));
  };

  const save = async () => {
    if (!version.trim()) {
      toast.error(lang === 'es' ? 'La version es obligatoria' : 'Version is required');
      return;
    }
    if (!date) {
      toast.error(lang === 'es' ? 'La fecha es obligatoria' : 'Date is required');
      return;
    }
    setSaving(true);
    try {
      const payload = { title, values, enabled_sections: enabled, version: version.trim(), date };
      // URS-F290: solo enviar el snapshot si el usuario ha tocado las secciones,
      // evitando reescrituras innecesarias.
      if (snapshotDirty && editedSections) {
        payload.template_snapshot = { ...(instance.template_snapshot || {}), sections: editedSections };
      }
      await axios.put(`${API_URL}/api/study-consents/${instance.study_consent_id}`, payload, { withCredentials: true });
      toast.success(lang === 'es' ? 'Cambios guardados' : 'Saved');
      onSaved();
    } catch (e) {
      toast.error(errText(e, lang === 'es' ? 'Error al guardar' : 'Save error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{lang === 'es' ? 'Configurar consentimiento' : 'Configure consent'}</DialogTitle>
          <DialogDescription>
            {lang === 'es'
              ? 'Edita el titulo, los datos del Investigador Principal y activa/desactiva las secciones del CI.'
              : 'Edit title, Principal Investigator data, and toggle the CI sections.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
          <div className="space-y-1">
            <Label className="text-xs">{lang === 'es' ? 'Titulo' : 'Title'}</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">
                {lang === 'es' ? 'Version' : 'Version'} <span className="text-red-500">*</span>
              </Label>
              <Input value={version} onChange={e => setVersion(e.target.value)} data-testid="consent-config-version" placeholder={lang === 'es' ? 'Ej. 1.0' : 'e.g. 1.0'} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">
                {lang === 'es' ? 'Fecha' : 'Date'} <span className="text-red-500">*</span>
              </Label>
              <Input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="consent-config-date" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">{lang === 'es' ? 'Nombre Investigador Principal' : 'Principal Investigator'}</Label>
              <Input value={values.investigator_name || ''} onChange={e => setValues(v => ({ ...v, investigator_name: e.target.value }))} data-testid="consent-config-ip-name" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{lang === 'es' ? 'DNI IP' : 'PI DNI'}</Label>
              <Input value={values.investigator_dni || ''} onChange={e => setValues(v => ({ ...v, investigator_dni: e.target.value }))} />
            </div>
            <div className="space-y-1 col-span-2">
              <Label className="text-xs">{lang === 'es' ? 'Email IP (para portal Validated ID)' : 'PI email (Validated ID portal)'}</Label>
              <Input type="email" value={values.investigator_email || ''} onChange={e => setValues(v => ({ ...v, investigator_email: e.target.value }))} data-testid="consent-config-ip-email" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{lang === 'es' ? 'Codigo de protocolo' : 'Study code'}</Label>
              <Input value={values.study_code || ''} onChange={e => setValues(v => ({ ...v, study_code: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{lang === 'es' ? 'Titulo del estudio' : 'Study title'}</Label>
              <Input value={values.study_title || ''} onChange={e => setValues(v => ({ ...v, study_title: e.target.value }))} />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs flex items-center gap-2">
              {lang === 'es' ? 'Secciones activas' : 'Enabled sections'}
              {snapshotDirty && (
                <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-200">
                  {lang === 'es' ? 'Texto modificado solo para este estudio' : 'Text customized for this study only'}
                </Badge>
              )}
            </Label>
            <div className="space-y-1.5">
              {sections.map(s => {
                const checked = enabled.includes(s.id);
                const isImg = s.kind === 'image_clauses';
                const disabled = isImg && !studyHasImages;
                const editable = ['static_text', 'withdrawal_block', 'clauses', 'image_clauses'].includes(s.kind);
                const isEditingThis = editingSectionId === s.id;
                return (
                  <div key={s.id} className={`border border-stone-200 rounded-sm p-2 ${disabled ? 'opacity-50' : ''}`}>
                    <div className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={disabled}
                        onChange={e => toggleSection(s.id, e.target.checked)}
                        data-testid={`consent-config-section-${s.id}`}
                      />
                      <span className="font-medium">{s.title || s.id}</span>
                      <Badge variant="outline" className="text-xs">{s.kind}</Badge>
                      {s.optional && <span className="text-xs text-amber-700">{lang === 'es' ? 'opcional' : 'optional'}</span>}
                      {disabled && (
                        <span className="text-xs text-rose-600">
                          {lang === 'es' ? '(activa "Estudio recoge imagenes" para habilitar)' : '(enable "study collects images" first)'}
                        </span>
                      )}
                      {editable && !disabled && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingSectionId(isEditingThis ? null : s.id)}
                          className="ml-auto h-7 text-[11px] gap-1.5"
                          data-testid={`consent-edit-text-${s.id}`}
                        >
                          <Pencil size={12} />
                          {isEditingThis
                            ? (lang === 'es' ? 'Cerrar' : 'Close')
                            : (lang === 'es' ? 'Editar texto' : 'Edit text')}
                        </Button>
                      )}
                    </div>
                    {isEditingThis && editable && (
                      <div className="mt-2 pl-6 space-y-2 border-l-2 border-amber-200">
                        {/* Editable title (only for sections that show a title) */}
                        {('title' in s) && (
                          <div className="space-y-1">
                            <Label className="text-[10px] text-stone-500 uppercase">{lang === 'es' ? 'Titulo de la seccion' : 'Section title'}</Label>
                            <Input value={s.title || ''} onChange={e => updateSection(s.id, { title: e.target.value })} className="h-8 text-sm" />
                          </div>
                        )}
                        {/* Free text sections */}
                        {('text' in s) && (
                          <div className="space-y-1">
                            <Label className="text-[10px] text-stone-500 uppercase">{lang === 'es' ? 'Texto' : 'Text'}</Label>
                            <Textarea
                              value={s.text || ''}
                              onChange={e => updateSection(s.id, { text: e.target.value })}
                              rows={Math.max(3, (s.text || '').split('\n').length)}
                              className="text-sm font-mono"
                              data-testid={`consent-section-text-${s.id}`}
                            />
                            <p className="text-[10px] text-stone-400">{lang === 'es' ? 'Puedes usar placeholders {volunteer_full_name}, {volunteer_dni}, {study_code}, etc.' : 'You can use placeholders like {volunteer_full_name}, {volunteer_dni}, {study_code}.'}</p>
                          </div>
                        )}
                        {/* Clauses sections */}
                        {Array.isArray(s.clauses) && s.clauses.length > 0 && (
                          <div className="space-y-2">
                            <Label className="text-[10px] text-stone-500 uppercase">{lang === 'es' ? 'Clausulas' : 'Clauses'}</Label>
                            {s.clauses.map(c => (
                              <div key={c.id} className="space-y-1 bg-stone-50 p-2 rounded-sm">
                                <div className="text-[10px] text-stone-400 font-mono">{c.id}{c.answer === 'yes_no' && ' · Sí/No'}</div>
                                <Textarea
                                  value={c.text || ''}
                                  onChange={e => updateClause(s.id, c.id, { text: e.target.value })}
                                  rows={2}
                                  className="text-sm"
                                  data-testid={`consent-clause-text-${s.id}-${c.id}`}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                        <p className="text-[10px] text-amber-700 italic">
                          {lang === 'es'
                            ? 'Los cambios solo afectan a este consentimiento del estudio. La plantilla del configurador no se modifica.'
                            : 'Changes only affect this study consent. The configurator template is not modified.'}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{lang === 'es' ? 'Cancelar' : 'Cancel'}</Button>
          <Button onClick={save} disabled={saving} data-testid="consent-config-save-btn">
            <FloppyDisk size={14} className="mr-1.5" />
            {saving ? '...' : (lang === 'es' ? 'Guardar' : 'Save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* --- Dialog: send to volunteer (dual signature) ---------------------- */
function SendToVolunteerDialog({ open, instance, onClose, onSent, volunteers, lang }) {
  const [volunteerId, setVolunteerId] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [signerUserId, setSignerUserId] = useState('');
  const [signers, setSigners] = useState([]);
  const [loadingSigners, setLoadingSigners] = useState(false);
  const [devices, setDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [sending, setSending] = useState(false);
  // URS-F239: busqueda por nombre dentro del selector de voluntarios.
  const [volSearch, setVolSearch] = useState('');

  useEffect(() => {
    if (!open) { setVolunteerId(''); setDeviceName(''); setSignerUserId(''); setSigners([]); setVolSearch(''); return; }
    let cancelled = false;
    (async () => {
      setLoadingDevices(true);
      setLoadingSigners(true);
      try {
        const [devRes, sigRes] = await Promise.all([
          axios.get(`${API_URL}/api/vidsigner/devices`, { withCredentials: true }),
          instance?.study_id
            ? axios.get(`${API_URL}/api/studies/${instance.study_id}/consent-signers`, { withCredentials: true })
            : Promise.resolve({ data: [] }),
        ]);
        if (!cancelled) {
          setDevices(Array.isArray(devRes?.data?.items) ? devRes.data.items : []);
          const sigList = Array.isArray(sigRes?.data) ? sigRes.data : [];
          setSigners(sigList);
          // URS-F494: preseleccion inteligente. Priorizamos, por este
          // orden:
          //   1) El signer cuyo email coincide con el email
          //      "configurado en el CI" (`values.investigator_email`)
          //      — asi el envio va SIEMPRE al email que aparece en la
          //      config del CI, aunque el orden del staff cambie.
          //   2) Sino, el primer IP del array staff (ya no
          //      alfabetico tras el fix backend).
          //   3) Sino, el primer signer con DNI+email completos.
          const cfgEmail = (instance?.values?.investigator_email || '').trim().toLowerCase();
          let chosen = null;
          if (cfgEmail) {
            chosen = sigList.find(s => (s.email || '').trim().toLowerCase() === cfgEmail && s.dni && s.email);
          }
          if (!chosen) {
            chosen = sigList.find(s => s.role === 'investigador_principal' && s.dni && s.email);
          }
          if (!chosen) {
            chosen = sigList.find(s => s.dni && s.email);
          }
          if (chosen) setSignerUserId(chosen.id);
        }
      } catch {
        if (!cancelled) { setDevices([]); setSigners([]); }
      } finally {
        if (!cancelled) { setLoadingDevices(false); setLoadingSigners(false); }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, instance?.study_id]);

  const syncDevices = async () => {
    setLoadingDevices(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/vidsigner/devices?sync=true`, { withCredentials: true });
      setDevices(Array.isArray(data?.items) ? data.items : []);
      toast.success(lang === 'es' ? 'Tabletas sincronizadas' : 'Tablets synced');
    } catch (e) {
      toast.error(errText(e, lang === 'es' ? 'Error sincronizando tabletas' : 'Tablet sync error'));
    } finally {
      setLoadingDevices(false);
    }
  };

  if (!instance) return null;

  const send = async () => {
    if (!volunteerId || !deviceName || !signerUserId) return;
    setSending(true);
    try {
      await axios.post(
        `${API_URL}/api/study-consents/${instance.study_consent_id}/send`,
        { volunteer_id: volunteerId, device_name: deviceName, signer_user_id: signerUserId },
        { withCredentials: true },
      );
      toast.success(lang === 'es' ? 'Documento enviado a la tableta' : 'Sent to tablet');
      onSent();
    } catch (e) {
      toast.error(errText(e, lang === 'es' ? 'Error al enviar' : 'Send error'));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{lang === 'es' ? 'Enviar a firmar' : 'Send to sign'}</DialogTitle>
          <DialogDescription>
            {lang === 'es'
              ? 'Doble firma: voluntario en tableta (biometrica) + Investigador (IP o Co-IP) remoto via portal Validated ID.'
              : 'Dual signature: volunteer on tablet (biometric) + Investigator (PI or Co-PI) remote via Validated ID portal.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">{lang === 'es' ? 'Voluntario destinatario' : 'Target volunteer'}</Label>
            {/* URS-F240: combobox unificado. El input filtra en vivo y la lista
                aparece debajo con los voluntarios que coinciden. Click selecciona;
                el nombre seleccionado queda visible en el input para edicion. */}
            {(() => {
              const sorted = [...volunteers].sort((a, b) => {
                const ca = a.study_volunteer_code || '';
                const cb = b.study_volunteer_code || '';
                const na = parseInt(ca.replace(/^V/i, ''), 10);
                const nb = parseInt(cb.replace(/^V/i, ''), 10);
                const aValid = !Number.isNaN(na);
                const bValid = !Number.isNaN(nb);
                if (aValid && bValid) return na - nb;
                if (aValid) return -1;
                if (bValid) return 1;
                const pdA = a.personal_data || {};
                const pdB = b.personal_data || {};
                const nameA = a.volunteer_name || a.display_name || `${pdA.nombre || ''} ${pdA.apellidos || ''}`.trim();
                const nameB = b.volunteer_name || b.display_name || `${pdB.nombre || ''} ${pdB.apellidos || ''}`.trim();
                return nameA.localeCompare(nameB);
              });
              const labelFor = (v) => {
                const pd = v.personal_data || {};
                const fullName = (v.volunteer_name || v.display_name || `${pd.nombre || ''} ${pd.apellidos || ''}`.trim() || v.volunteer_id);
                return v.study_volunteer_code ? `${v.study_volunteer_code} · ${fullName}` : fullName;
              };
              const selected = sorted.find(v => v.volunteer_id === volunteerId);
              const q = volSearch.trim().toLowerCase();
              const filtered = q
                ? sorted.filter(v => labelFor(v).toLowerCase().includes(q))
                : sorted;
              // Mostrar la lista cuando el usuario esta escribiendo o cuando aun
              // no se ha seleccionado nadie. Tras seleccionar, mantenemos el
              // dropdown oculto hasta que el usuario vuelva a interactuar con el
              // input (focus o tecla).
              const showList = volSearch.length > 0 || !volunteerId;
              return (
                <div className="relative">
                  <Input
                    data-testid="send-consent-volunteer-search"
                    value={volSearch || (selected ? labelFor(selected) : '')}
                    onFocus={() => { if (selected && !volSearch) setVolSearch(''); }}
                    onChange={e => { setVolSearch(e.target.value); if (volunteerId) setVolunteerId(''); }}
                    placeholder={lang === 'es' ? 'Buscar por nombre o codigo (V1, V2...)' : 'Search by name or code (V1, V2...)'}
                    className="bg-white text-sm h-8"
                  />
                  {showList && volunteers.length > 0 && (
                    <div className="absolute z-50 left-0 right-0 mt-1 max-h-56 overflow-y-auto bg-white border border-stone-200 rounded-sm shadow-md" data-testid="send-consent-volunteer-list">
                      {filtered.length === 0 ? (
                        <div className="px-3 py-2 text-xs text-stone-500">{lang === 'es' ? 'Sin coincidencias' : 'No matches'}</div>
                      ) : (
                        filtered.map(v => (
                          <button
                            type="button"
                            key={v.volunteer_id}
                            data-testid={`send-consent-volunteer-option-${v.volunteer_id}`}
                            onClick={() => { setVolunteerId(v.volunteer_id); setVolSearch(''); }}
                            className={`w-full text-left px-3 py-1.5 text-sm hover:bg-stone-50 ${v.volunteer_id === volunteerId ? 'bg-[#1f3e80]/5 text-[#1f3e80] font-semibold' : 'text-stone-700'}`}
                          >
                            {labelFor(v)}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })()}
            {volunteers.length === 0 && (
              <p className="text-xs text-amber-700">
                {lang === 'es'
                  ? 'No hay voluntarios incluidos en este estudio.'
                  : 'No included volunteers in this study.'}
              </p>
            )}
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{lang === 'es' ? 'Investigador firmante' : 'Investigator signer'}</Label>
            <Select value={signerUserId} onValueChange={setSignerUserId} disabled={loadingSigners || signers.length === 0}>
              <SelectTrigger data-testid="send-consent-signer-select">
                <SelectValue placeholder={loadingSigners ? (lang === 'es' ? 'Cargando...' : 'Loading...') : (lang === 'es' ? 'Selecciona quien firma' : 'Select signer')} />
              </SelectTrigger>
              <SelectContent>
                {signers.map(s => {
                  const roleLabel = s.role === 'investigador_principal'
                    ? (lang === 'es' ? 'IP' : 'PI')
                    : (lang === 'es' ? 'Co-IP' : 'Co-PI');
                  const missing = !s.dni || !s.email;
                  const fullName = [s.name, s.surname1, s.surname2].filter(Boolean).join(' ').trim() || s.email;
                  // URS-F494: mostrar el email en el item para que el
                  // operador vea A QUE EMAIL se enviara la firma remota
                  // (VIDsigner solo la muestra en el portal si ese email
                  // esta registrado como usuario en la suscripcion).
                  return (
                    <SelectItem key={s.id} value={s.id} disabled={missing}>
                      <span className="font-medium">[{roleLabel}] {fullName}</span>
                      {s.email ? <span className="text-stone-500"> &nbsp;·&nbsp; {s.email}</span> : null}
                      {s.dni ? <span className="text-stone-500"> &nbsp;·&nbsp; {s.dni}</span> : null}
                      {missing ? (lang === 'es' ? ' - falta DNI/email' : ' - missing DNI/email') : ''}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            {!loadingSigners && signers.length === 0 && (
              <p className="text-xs text-amber-700">
                {lang === 'es'
                  ? 'Este estudio no tiene Investigador Principal ni Co-Investigadores asignados. Asignalos en la pestana Personal del estudio.'
                  : 'This study has no Principal Investigator or Co-Investigators assigned. Assign them in the study Personnel tab.'}
              </p>
            )}
            {signerUserId && (() => {
              const s = signers.find(x => x.id === signerUserId);
              if (!s) return null;
              const fullName = [s.name, s.surname1, s.surname2].filter(Boolean).join(' ').trim();
              // URS-F494: advertencia si el signer elegido tiene un
              // email distinto al configurado en el CI (values
              // .investigator_email). Esto tipicamente indica que el
              // operador esta enviando a un IP distinto del que
              // aparece en el bloque de firma del PDF.
              const cfgEmail = (instance?.values?.investigator_email || '').trim().toLowerCase();
              const curEmail = (s.email || '').trim().toLowerCase();
              const mismatch = cfgEmail && curEmail && cfgEmail !== curEmail;
              return (
                <>
                  <p className="text-[11px] text-stone-600">
                    {lang === 'es' ? 'Aparecera en el PDF:' : 'Will appear on PDF:'}{' '}
                    <b>{fullName}</b>{s.dni ? ` - DNI ${s.dni}` : ''}
                    {s.email ? <><br/>{lang === 'es' ? 'Firma remota (Validated ID) a:' : 'Remote signature (Validated ID) to:'} <b>{s.email}</b> — {lang === 'es' ? 'debe existir como usuario en la suscripcion Validated ID de la CRO.' : 'must exist as a user in the Validated ID subscription.'}</> : null}
                  </p>
                  {mismatch && (
                    <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-300 rounded px-2 py-1 mt-1" data-testid="signer-email-mismatch-warning">
                      {lang === 'es'
                        ? `El email configurado en este CI es "${instance.values.investigator_email}" pero vas a enviar la firma a "${s.email}". Confirma que es lo que quieres.`
                        : `The email configured in this CI is "${instance.values.investigator_email}" but you are sending the signature to "${s.email}". Confirm this is what you want.`}
                    </p>
                  )}
                </>
              );
            })()}
          </div>
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-xs">{lang === 'es' ? 'Tableta destino (Validated ID)' : 'Target tablet (Validated ID)'}</Label>
              <button type="button" onClick={syncDevices} className="text-[10px] text-blue-700 hover:underline disabled:opacity-50" disabled={loadingDevices} data-testid="send-consent-sync-devices-btn">
                {loadingDevices ? (lang === 'es' ? 'Sincronizando...' : 'Syncing...') : (lang === 'es' ? 'Sincronizar tabletas' : 'Sync tablets')}
              </button>
            </div>
            <Select value={deviceName} onValueChange={setDeviceName} disabled={devices.length === 0 || loadingDevices}>
              <SelectTrigger data-testid="send-consent-device-select">
                <SelectValue placeholder={loadingDevices ? (lang === 'es' ? 'Cargando...' : 'Loading...') : (lang === 'es' ? 'Selecciona una tableta' : 'Select a tablet')} />
              </SelectTrigger>
              <SelectContent>
                {devices.map(d => (
                  <SelectItem key={d.device_name} value={d.device_name}>
                    {d.device_name}{d.device_description ? ` - ${d.device_description}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!loadingDevices && devices.length === 0 && (
              <p className="text-xs text-amber-700">
                {lang === 'es'
                  ? 'No hay tabletas registradas. Pulsa "Sincronizar tabletas" para descargarlas de Validated ID.'
                  : 'No tablets registered. Click "Sync tablets" to fetch them from Validated ID.'}
              </p>
            )}
          </div>
          <p className="text-xs text-stone-500">
            {lang === 'es'
              ? 'El voluntario firmara presencialmente en esta tableta; el Investigador seleccionado recibira un email para firmar en remoto via el portal Validated ID.'
              : 'Volunteer signs in person on this tablet; the selected Investigator gets an email to sign remotely via the Validated ID portal.'}
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{lang === 'es' ? 'Cancelar' : 'Cancel'}</Button>
          <Button onClick={send} disabled={!volunteerId || !deviceName || !signerUserId || sending} data-testid="send-consent-confirm-btn">
            <PaperPlaneTilt size={14} className="mr-1.5" />
            {sending ? '...' : (lang === 'es' ? 'Enviar' : 'Send')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* --- Sub-panel: signature tracking dashboard ------------------------- */
const STATUS_STYLES = {
  signed:    { bg: 'bg-emerald-100 text-emerald-800 border-emerald-300', es: 'Firmado',           en: 'Signed' },
  pending:   { bg: 'bg-amber-100 text-amber-800 border-amber-300',       es: 'Pendiente firma',    en: 'Pending signature' },
  not_sent:  { bg: 'bg-stone-100 text-stone-700 border-stone-300',       es: 'Sin enviar',         en: 'Not sent' },
  rejected:  { bg: 'bg-rose-100 text-rose-800 border-rose-300',          es: 'Rechazado',          en: 'Rejected' },
  expired:   { bg: 'bg-orange-100 text-orange-800 border-orange-300',    es: 'Caducado',           en: 'Expired' },
  cancelled: { bg: 'bg-stone-200 text-stone-700 border-stone-300',       es: 'Cancelado',          en: 'Cancelled' },
};

function ConsentTrackingPanel({ studyConsentId, studyId, lang, highlightVolunteerId }) {
  const { isAdmin } = usePermissions();
  const rowRefs = useRef({});
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  // Preview en popup (no descarga) del PDF firmado.
  const [preview, setPreview] = useState(null); // { url, name }
  // URS-F494c: dialogo admin para reasignar el IP de un CI ya firmado
  // por el voluntario pero con el IP incorrecto.
  const [reassignFor, setReassignFor] = useState(null); // { volunteerId, studyVolunteerCode, currentIpEmail }
  const [reassignSigners, setReassignSigners] = useState([]);
  const [reassignForm, setReassignForm] = useState({ new_signer_user_id: '', reason: '', device_name: 'Portal' });
  const [reassignBusy, setReassignBusy] = useState(false);
  // URS-F494d: dialogo para cancelar un envio bloqueado (firma en papel
  // o reenvio manual). Solo elimina el registro del tracking; no cancela
  // en Validated ID (no hay API oficial).
  const [cancelFor, setCancelFor] = useState(null); // { volunteerId, studyVolunteerCode, ipEmail }
  const [cancelReason, setCancelReason] = useState('');
  const [cancelBusy, setCancelBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(
        `${API_URL}/api/study-consents/${studyConsentId}/tracking`,
        { withCredentials: true },
      );
      setData(d);
      return d;
    } catch {
      toast.error(lang === 'es' ? 'Error al cargar seguimiento' : 'Error loading tracking');
      return null;
    } finally {
      setLoading(false);
    }
  }, [studyConsentId, lang]);

  const syncFromVidsigner = useCallback(async (silent = false) => {
    setSyncing(true);
    try {
      const { data: res } = await axios.post(
        `${API_URL}/api/vidsigner/refresh-pending?study_id=${encodeURIComponent(studyId)}`,
        {},
        { withCredentials: true },
      );
      if (!silent) {
        const a = res?.archived || 0;
        const r = res?.refreshed || 0;
        toast.success(
          lang === 'es'
            ? `Sincronizado: ${r} actualizado(s), ${a} archivado(s)`
            : `Synced: ${r} refreshed, ${a} archived`,
        );
      }
      await load();
    } catch (e) {
      if (!silent) toast.error(errText(e, lang === 'es' ? 'Error sincronizando' : 'Sync error'));
    } finally {
      setSyncing(false);
    }
  }, [studyId, lang, load]);

  // Auto-sync on first open when there are sent-but-not-archived docs.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const d = await load();
      if (cancelled || !d) return;
      const hasPending = (d.items || []).some(
        it => it.status !== 'not_sent' && !it.archived_to_volunteer,
      );
      if (hasPending) {
        await syncFromVidsigner(true);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studyConsentId]);

  // URS-F469d: scroll suave a la fila del voluntario resaltado (llegada
  // desde la alerta "Consentimientos pendientes" del Study Dashboard).
  useEffect(() => {
    if (!highlightVolunteerId || !data) return;
    const el = rowRefs.current[highlightVolunteerId];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightVolunteerId, data]);

  const downloadSignedPdf = async (fileId, fileName) => {
    try {
      const resp = await axios.get(`${API_URL}/api/files/download/${fileId}`, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url; a.download = fileName || 'consentimiento_firmado.pdf';
      document.body.appendChild(a); a.click();
      setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 500);
    } catch {
      toast.error(lang === 'es' ? 'Error descargando PDF' : 'PDF download error');
    }
  };

  const previewSignedPdf = async (fileId, fileName) => {
    try {
      const resp = await axios.get(`${API_URL}/api/files/download/${fileId}`, { withCredentials: true, responseType: 'blob' });
      // Forzar mime application/pdf por si el blob no lo trae explicito.
      const blob = resp.data.type === 'application/pdf' ? resp.data : new Blob([resp.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPreview({ url, name: fileName || 'consentimiento_firmado.pdf' });
    } catch {
      toast.error(lang === 'es' ? 'Error abriendo PDF' : 'PDF preview error');
    }
  };

  const closePreview = () => {
    if (preview?.url) {
      try { URL.revokeObjectURL(preview.url); } catch { /* noop */ }
    }
    setPreview(null);
  };

  // URS-F494c: carga los signers disponibles al abrir el dialogo de
  // reasignacion. Reutiliza el endpoint existente /consent-signers.
  useEffect(() => {
    if (!reassignFor) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: sigList } = await axios.get(`${API_URL}/api/studies/${studyId}/consent-signers`, { withCredentials: true });
        if (cancelled) return;
        const list = Array.isArray(sigList) ? sigList : [];
        setReassignSigners(list);
        const preferred = list.find(s => s.dni && s.email && (s.email || '').trim().toLowerCase() !== (reassignFor.currentIpEmail || '').trim().toLowerCase());
        setReassignForm(f => ({ ...f, new_signer_user_id: preferred?.id || (list.find(s => s.dni && s.email)?.id || '') }));
      } catch (e) {
        toast.error(lang === 'es' ? 'Error cargando firmantes disponibles' : 'Error loading signers');
      }
    })();
    return () => { cancelled = true; };
  }, [reassignFor, studyId, lang]);

  const submitReassignIP = async () => {
    if (!reassignFor) return;
    const reason = (reassignForm.reason || '').trim();
    if (reason.length < 10) {
      toast.error(lang === 'es' ? 'El motivo debe tener al menos 10 caracteres' : 'Reason must be at least 10 chars');
      return;
    }
    if (!reassignForm.new_signer_user_id) {
      toast.error(lang === 'es' ? 'Selecciona el nuevo firmante' : 'Select the new signer');
      return;
    }
    setReassignBusy(true);
    try {
      const { data: res } = await axios.post(
        `${API_URL}/api/study-consents/${studyConsentId}/reassign-ip-signer`,
        {
          volunteer_id: reassignFor.volunteerId,
          new_signer_user_id: reassignForm.new_signer_user_id,
          device_name: (reassignForm.device_name || 'Portal').trim(),
          reason,
        },
        { withCredentials: true },
      );
      toast.success(lang === 'es'
        ? `Reasignado. Nuevo firmante: ${res.new_ip_email}`
        : `Reassigned. New signer: ${res.new_ip_email}`);
      setReassignFor(null);
      setReassignForm({ new_signer_user_id: '', reason: '', device_name: 'Portal' });
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || (lang === 'es' ? 'Error al reasignar' : 'Error reassigning');
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setReassignBusy(false);
    }
  };

  // URS-F494d: cancelar un envio bloqueado en el tracking (firma en papel).
  const submitCancelSend = async () => {
    if (!cancelFor) return;
    const reason = (cancelReason || '').trim();
    if (reason.length < 10) {
      toast.error(lang === 'es' ? 'El motivo debe tener al menos 10 caracteres' : 'Reason must be at least 10 chars');
      return;
    }
    setCancelBusy(true);
    try {
      await axios.post(
        `${API_URL}/api/study-consents/${studyConsentId}/cancel-send`,
        { volunteer_id: cancelFor.volunteerId, reason },
        { withCredentials: true },
      );
      toast.success(lang === 'es' ? 'Envio cancelado del tracking' : 'Send cancelled from tracking');
      setCancelFor(null);
      setCancelReason('');
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || (lang === 'es' ? 'Error al cancelar' : 'Error cancelling');
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setCancelBusy(false);
    }
  };

  if (loading && !data) {
    return <p className="text-xs text-stone-500 pt-2 border-t border-stone-100">{lang === 'es' ? 'Cargando seguimiento...' : 'Loading tracking...'}</p>;
  }
  if (!data) return null;
  const { total_volunteers_in_study: total, counts, items: rawItems } = data;
  const sheetsById = data?.sheets_by_id || {};
  const pct = (n) => total ? Math.round((n / total) * 100) : 0;

  // URS-F422: reutilizamos la misma paleta de badges de URS-F421 para pintar
  // "HIP enviada" con el color adecuado segun label (adulto/tutor/menor...).
  const getSheetBadgeStyle = (label) => {
    const s = (label || '').toLowerCase();
    if (/(tutor|guardian|padre|madre|responsable)/.test(s))
      return 'bg-violet-100 text-violet-700 border-violet-300';
    if (/(menor|min[oó]r|adolescente|ni[nñ]o)/.test(s))
      return 'bg-amber-100 text-amber-700 border-amber-300';
    if (/(adulto|mayor|general|principal)/.test(s))
      return 'bg-sky-100 text-sky-700 border-sky-300';
    return 'bg-stone-100 text-stone-700 border-stone-300';
  };

  // URS-F239: ordenar el seguimiento por V-code numerico ascendente (V1, V2,
  // V3, ... V10, V11). Items sin codigo van al final por nombre.
  const items = [...(rawItems || [])].sort((a, b) => {
    const ca = a.study_volunteer_code || '';
    const cb = b.study_volunteer_code || '';
    const na = parseInt(ca.replace(/^V/i, ''), 10);
    const nb = parseInt(cb.replace(/^V/i, ''), 10);
    const aValid = !Number.isNaN(na);
    const bValid = !Number.isNaN(nb);
    if (aValid && bValid) return na - nb;
    if (aValid) return -1;
    if (bValid) return 1;
    return (a.volunteer_name || '').localeCompare(b.volunteer_name || '');
  });

  const SignerBadge = ({ sig, label }) => {
    const status = sig?.status || 'pending';
    const style =
      status === 'signed' ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
      : status === 'rejected' ? 'bg-rose-100 text-rose-800 border-rose-300'
      : status === 'not_sent' ? 'bg-stone-100 text-stone-600 border-stone-300'
      : 'bg-amber-100 text-amber-800 border-amber-300';
    const text =
      status === 'signed' ? (lang === 'es' ? 'Firmado' : 'Signed')
      : status === 'rejected' ? (lang === 'es' ? 'Rechazado' : 'Rejected')
      : status === 'not_sent' ? (lang === 'es' ? '—' : '—')
      : (lang === 'es' ? 'Pendiente' : 'Pending');
    // URS-F494: mostrar el email del firmante en tooltip para poder
    // verificar visualmente a que direccion se envio la firma remota.
    const tip = sig?.email ? `${sig.name || ''} · ${sig.email}` : (sig?.name || '');
    return (
      <span className={`inline-flex items-center gap-1 border rounded px-1.5 py-0.5 text-[10px] ${style}`} title={tip}>
        <b className="opacity-70">{label}:</b> {text}
      </span>
    );
  };

  return (
    <div className="pt-3 mt-1 border-t border-stone-100 space-y-3" data-testid={`consent-tracking-${studyConsentId}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className={`${STATUS_STYLES.signed.bg} border text-xs`}>
            {STATUS_STYLES.signed[lang === 'es' ? 'es' : 'en']}: <b className="ml-1">{counts.signed}</b> ({pct(counts.signed)}%)
          </Badge>
          <Badge className={`${STATUS_STYLES.pending.bg} border text-xs`}>
            {STATUS_STYLES.pending[lang === 'es' ? 'es' : 'en']}: <b className="ml-1">{counts.pending}</b>
          </Badge>
          <Badge className={`${STATUS_STYLES.not_sent.bg} border text-xs`}>
            {STATUS_STYLES.not_sent[lang === 'es' ? 'es' : 'en']}: <b className="ml-1">{counts.not_sent}</b>
          </Badge>
          {counts.rejected > 0 && (
            <Badge className={`${STATUS_STYLES.rejected.bg} border text-xs`}>
              {STATUS_STYLES.rejected[lang === 'es' ? 'es' : 'en']}: <b className="ml-1">{counts.rejected}</b>
            </Badge>
          )}
          {(counts.expired > 0 || counts.cancelled > 0) && (
            <Badge className={`${STATUS_STYLES.expired.bg} border text-xs`}>
              {(STATUS_STYLES.expired[lang === 'es' ? 'es' : 'en'])} / {(STATUS_STYLES.cancelled[lang === 'es' ? 'es' : 'en'])}: <b className="ml-1">{counts.expired + counts.cancelled}</b>
            </Badge>
          )}
          <span className="text-xs text-stone-500">
            · {total} {lang === 'es' ? 'voluntarios incluidos' : 'included volunteers'}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" onClick={() => syncFromVidsigner(false)} disabled={syncing} className="gap-1 h-7 text-xs" data-testid={`consent-tracking-sync-${studyConsentId}`}>
            <ArrowsClockwise size={12} className={syncing ? 'animate-spin' : ''} />
            {syncing ? (lang === 'es' ? 'Sincronizando...' : 'Syncing...') : (lang === 'es' ? 'Sincronizar firmas' : 'Sync signatures')}
          </Button>
        </div>
      </div>

      {/* Progress bar (signed / total) */}
      <div className="h-2 bg-stone-100 rounded overflow-hidden flex">
        <div className="bg-emerald-500" style={{ width: `${pct(counts.signed)}%` }} title={`${counts.signed} firmados`} />
        <div className="bg-amber-400" style={{ width: `${pct(counts.pending)}%` }} title={`${counts.pending} pendientes`} />
        <div className="bg-rose-400" style={{ width: `${pct(counts.rejected + counts.expired + counts.cancelled)}%` }} />
      </div>

      {items.length === 0 ? (
        <p className="text-xs text-stone-500">{lang === 'es' ? 'No hay voluntarios incluidos en este estudio todavia.' : 'No included volunteers in this study yet.'}</p>
      ) : (
        <div className="max-h-72 overflow-y-auto border border-stone-100 rounded">
          <table className="w-full text-xs">
            <thead className="bg-stone-50 sticky top-0">
              <tr className="text-left text-stone-600">
                <th className="px-2 py-1.5 font-medium">{lang === 'es' ? 'Cod.' : 'Code'}</th>
                <th className="px-2 py-1.5 font-medium">{lang === 'es' ? 'Voluntario' : 'Volunteer'}</th>
                <th className="px-2 py-1.5 font-medium">{lang === 'es' ? 'HIP enviada' : 'IS sent'}</th>
                <th className="px-2 py-1.5 font-medium">{lang === 'es' ? 'Firma Voluntario' : 'Volunteer signature'}</th>
                <th className="px-2 py-1.5 font-medium">{lang === 'es' ? 'Firma IP' : 'PI signature'}</th>
                <th className="px-2 py-1.5 font-medium">{lang === 'es' ? 'Enviado' : 'Sent'}</th>
                <th className="px-2 py-1.5 font-medium">{lang === 'es' ? 'PDF' : 'PDF'}</th>
              </tr>
            </thead>
            <tbody>
              {items.map(it => {
                const fmtDateTime = (v) => v ? new Date(v).toLocaleString(lang === 'es' ? 'es-ES' : 'en-US', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-';
                const fmtDateOnly = (v) => v ? new Date(v).toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';
                const volSig = it.status === 'not_sent' ? { status: 'not_sent' } : it.volunteer_signature;
                const ipSig = it.status === 'not_sent' ? { status: 'not_sent' } : it.investigator_signature;
                return (
                  <tr key={it.volunteer_id}
                    ref={el => { if (el) rowRefs.current[it.volunteer_id] = el; }}
                    className={`border-t border-stone-100 hover:bg-stone-50 ${(it.study_status === 'excluido' || it.study_status === 'abandono' || it.study_status === 'retirado') ? 'opacity-70' : ''} ${highlightVolunteerId === it.volunteer_id ? 'ring-2 ring-amber-400 bg-amber-50/60' : ''}`} data-testid={`consent-tracking-row-${it.volunteer_id}`}>
                    <td className="px-2 py-1.5 text-stone-700">{it.study_volunteer_code || '-'}</td>
                    <td className="px-2 py-1.5 text-stone-900">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span>{it.volunteer_name}</span>
                        {(it.study_status === 'excluido' || it.study_status === 'abandono' || it.study_status === 'retirado') && (
                          <Badge
                            variant="outline"
                            className={`text-[9px] uppercase tracking-wide ${
                              it.study_status === 'excluido' ? 'bg-red-50 text-red-700 border-red-200'
                              : it.study_status === 'retirado' ? 'bg-orange-50 text-orange-700 border-orange-200'
                              : 'bg-amber-50 text-amber-700 border-amber-200'
                            }`}
                            data-testid={`consent-tracking-badge-${it.volunteer_id}`}
                          >
                            {it.study_status === 'excluido' ? (lang === 'es' ? 'Excluido' : 'Excluded')
                              : it.study_status === 'retirado' ? (lang === 'es' ? 'Retirado' : 'Withdrawn')
                              : (lang === 'es' ? 'Abandono' : 'Withdrawal')}
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-1.5">
                      {(() => {
                        const sheetIds = it.info_sheet_sent_sheet_ids || [];
                        const sentAt = it.info_sheet_sent_at;
                        if (!sentAt && sheetIds.length === 0) {
                          return (
                            <Badge variant="outline" className="text-[10px] border-stone-300 text-stone-700 bg-stone-50" data-testid={`consent-tracking-hip-none-${it.volunteer_id}`}>
                              {lang === 'es' ? 'Entrega manual' : 'Manual delivery'}
                            </Badge>
                          );
                        }
                        const dateStr = sentAt ? new Date(sentAt).toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';
                        if (sheetIds.length === 0) {
                          return (
                            <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-700 bg-emerald-50" title={dateStr} data-testid={`consent-tracking-hip-legacy-${it.volunteer_id}`}>
                              {lang === 'es' ? 'HIP enviada' : 'IS sent'}
                            </Badge>
                          );
                        }
                        return (
                          <div className="flex flex-wrap gap-1">
                            {sheetIds.map(sid => {
                              const sh = sheetsById[sid];
                              const label = sh?.label || (lang === 'es' ? 'Hoja' : 'Sheet');
                              const filename = sh?.filename || '';
                              return (
                                <Badge
                                  key={sid}
                                  variant="outline"
                                  className={`text-[10px] ${getSheetBadgeStyle(label)}`}
                                  title={`${dateStr}${filename ? ' · ' + filename : ''}`}
                                  data-testid={`consent-tracking-hip-${it.volunteer_id}-${sid}`}
                                >
                                  {label}
                                </Badge>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-2 py-1.5">
                      <SignerBadge sig={volSig} label={lang === 'es' ? 'Vol.' : 'Vol.'} />
                    </td>
                    <td className="px-2 py-1.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <SignerBadge sig={ipSig} label={lang === 'es' ? 'IP' : 'PI'} />
                        {/* URS-F494d: cancelar envio bloqueado (firma en papel).
                            El boton "Reasignar IP" fue retirado porque Validated ID
                            no permite descargar el PDF parcial (HTTP 409 en /signeddocuments/{gui}
                            si falta un firmante), asi que la reasignacion sin re-firmar
                            no es tecnicamente posible. */}
                        {isAdmin && it.status !== 'not_sent' && it.status !== 'signed' && !it.archived_to_volunteer && (
                          <button
                            type="button"
                            onClick={() => { setCancelFor({ volunteerId: it.volunteer_id, studyVolunteerCode: it.study_volunteer_code, ipEmail: ipSig?.email || '' }); setCancelReason(''); }}
                            className="text-[10px] text-rose-700 border border-rose-300 bg-rose-50 hover:bg-rose-100 rounded px-1.5 py-0.5"
                            data-testid={`consent-cancel-send-${it.volunteer_id}`}
                            title={lang === 'es' ? 'Cancelar envio del tracking (firma en papel)' : 'Cancel send from tracking (paper signature)'}
                          >
                            {lang === 'es' ? 'Eliminar envio' : 'Delete send'}
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-1.5 text-stone-500">{fmtDateTime(it.sent_at)}</td>
                    <td className="px-2 py-1.5">
                      {it.archived_to_volunteer && it.archived_file_id ? (
                        <div className="inline-flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => previewSignedPdf(it.archived_file_id, `CI_${it.study_volunteer_code || it.volunteer_id}.pdf`)}
                            className="inline-flex items-center gap-1 text-[#1f3e80] hover:text-[#2a4f8a] hover:underline"
                            data-testid={`consent-tracking-preview-${it.volunteer_id}`}
                            title={lang === 'es' ? 'Ver PDF sin descargar' : 'Preview PDF without download'}
                          >
                            <Eye size={12} />
                            {lang === 'es' ? 'Ver' : 'View'}
                          </button>
                          <button
                            type="button"
                            onClick={() => downloadSignedPdf(it.archived_file_id, `CI_${it.study_volunteer_code || it.volunteer_id}.pdf`)}
                            className="inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-900 hover:underline"
                            data-testid={`consent-tracking-download-${it.volunteer_id}`}
                            title={lang === 'es' ? 'Descargar PDF firmado' : 'Download signed PDF'}
                          >
                            <DownloadSimple size={12} />
                            {lang === 'es' ? 'Descargar' : 'Download'}
                          </button>
                        </div>
                      ) : (
                        <span className="text-stone-400">-</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* URS-F494c: dialogo admin para reasignar el firmante IP de un
          CI ya firmado por el voluntario. Descarga el PDF partial de
          Validated ID y crea un nuevo doc con SOLO el IP correcto — sin
          re-firmar el voluntario. */}
      <Dialog open={!!reassignFor} onOpenChange={(o) => { if (!o) { setReassignFor(null); } }}>
        <DialogContent className="max-w-xl" data-testid="consent-reassign-ip-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm">
              {lang === 'es' ? 'Reasignar firmante IP (admin)' : 'Reassign PI signer (admin)'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-stone-700">
              {lang === 'es'
                ? 'Voluntario: '
                : 'Volunteer: '}
              <b>{reassignFor?.studyVolunteerCode || reassignFor?.volunteerId}</b>
            </p>
            <p className="text-stone-700">
              {lang === 'es' ? 'Email IP actual (huerfano): ' : 'Current PI email (orphan): '}
              <code className="text-rose-700">{reassignFor?.currentIpEmail || '—'}</code>
            </p>
            <div className="rounded bg-amber-50 border border-amber-300 text-[11px] text-amber-900 p-2">
              {lang === 'es'
                ? 'Este flujo descarga el PDF actual desde Validated ID (con la firma del voluntario ya embebida) y lo reenvia con el nuevo IP como unico firmante remoto. El voluntario NO tendra que firmar de nuevo. La accion queda auditada.'
                : 'This downloads the current PDF from Validated ID (volunteer signature already embedded) and resends it with the new PI as the sole remote signer. The volunteer will NOT need to sign again. Action is audited.'}
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">{lang === 'es' ? 'Nuevo firmante' : 'New signer'}</label>
              <Select value={reassignForm.new_signer_user_id} onValueChange={(v) => setReassignForm(f => ({ ...f, new_signer_user_id: v }))}>
                <SelectTrigger data-testid="consent-reassign-signer-select">
                  <SelectValue placeholder={lang === 'es' ? 'Selecciona...' : 'Select...'} />
                </SelectTrigger>
                <SelectContent>
                  {reassignSigners.map(s => {
                    const roleLabel = s.role === 'investigador_principal' ? (lang === 'es' ? 'IP' : 'PI') : (lang === 'es' ? 'Co-IP' : 'Co-PI');
                    const missing = !s.dni || !s.email;
                    const name = [s.name, s.surname1, s.surname2].filter(Boolean).join(' ').trim() || s.email;
                    return (
                      <SelectItem key={s.id} value={s.id} disabled={missing}>
                        <span className="font-medium">[{roleLabel}] {name}</span>
                        {s.email ? <span className="text-stone-500"> · {s.email}</span> : null}
                        {s.dni ? <span className="text-stone-500"> · {s.dni}</span> : null}
                        {missing ? (lang === 'es' ? ' — falta DNI/email' : ' — missing DNI/email') : ''}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">{lang === 'es' ? 'Nombre del dispositivo (para audit)' : 'Device name (for audit)'}</label>
              <Input value={reassignForm.device_name} onChange={(e) => setReassignForm(f => ({ ...f, device_name: e.target.value }))} data-testid="consent-reassign-device" />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">{lang === 'es' ? 'Motivo (obligatorio, minimo 10 caracteres)' : 'Reason (required, min 10 chars)'}</label>
              <Textarea rows={3} value={reassignForm.reason} onChange={(e) => setReassignForm(f => ({ ...f, reason: e.target.value }))} data-testid="consent-reassign-reason" />
              <p className="text-[10px] text-stone-500">
                {lang === 'es'
                  ? 'Ej: "IP original sin cuenta en Validated ID; reenvio al IP correcto"'
                  : 'Ex: "Original PI has no Validated ID account; resend to correct PI"'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReassignFor(null)} data-testid="consent-reassign-cancel">
              {lang === 'es' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button onClick={submitReassignIP} disabled={reassignBusy || !reassignForm.new_signer_user_id || (reassignForm.reason || '').trim().length < 10} data-testid="consent-reassign-submit">
              {reassignBusy ? '...' : (lang === 'es' ? 'Reasignar y reenviar al IP' : 'Reassign and resend to PI')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* URS-F494d: dialogo para cancelar un envio bloqueado (firma en papel). */}
      <Dialog open={!!cancelFor} onOpenChange={(o) => { if (!o) { setCancelFor(null); setCancelReason(''); } }}>
        <DialogContent className="max-w-lg" data-testid="consent-cancel-send-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm">
              {lang === 'es' ? 'Cancelar envio del tracking' : 'Cancel send from tracking'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-xs">
            <p>{lang === 'es' ? 'Voluntario:' : 'Volunteer:'} <b>{cancelFor?.studyVolunteerCode || cancelFor?.volunteerId}</b></p>
            {cancelFor?.ipEmail && (
              <p>{lang === 'es' ? 'Email IP actual:' : 'Current PI email:'} <code className="text-rose-700">{cancelFor.ipEmail}</code></p>
            )}
            <div className="rounded-sm border border-amber-200 bg-amber-50 p-2 text-[11px] leading-snug text-amber-900">
              {lang === 'es'
                ? 'Esto elimina el envio del panel de seguimiento para que puedas firmar el CI en papel o reenviarlo desde cero. El documento no se cancela en Validated ID (no existe API oficial), pero queda huerfano y sin efecto en el tracking. La accion queda auditada.'
                : 'This removes the send from the tracking panel so you can sign on paper or resend from scratch. The document is not cancelled in Validated ID (no official API) but is orphaned and has no effect on tracking. The action is audited.'}
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">{lang === 'es' ? 'Motivo (obligatorio, minimo 10 caracteres)' : 'Reason (required, min 10 chars)'}</label>
              <Textarea rows={3} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} data-testid="consent-cancel-reason" />
              <p className="text-[10px] text-stone-500">
                {lang === 'es'
                  ? 'Ej: "IP sin cuenta en Validated ID; se firmara en papel"'
                  : 'Ex: "PI has no Validated ID account; will sign on paper"'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCancelFor(null); setCancelReason(''); }} data-testid="consent-cancel-send-back">
              {lang === 'es' ? 'Volver' : 'Back'}
            </Button>
            <Button onClick={submitCancelSend} disabled={cancelBusy || (cancelReason || '').trim().length < 10} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="consent-cancel-send-submit">
              {cancelBusy ? '...' : (lang === 'es' ? 'Eliminar del tracking' : 'Delete from tracking')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview de PDF firmado (sin descarga) */}
      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) closePreview(); }}>
        <DialogContent className="max-w-5xl w-[90vw] h-[88vh] p-4 gap-2 flex flex-col" data-testid="consent-signed-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-stone-900 text-sm">
              <FilePdf size={18} className="text-rose-600" />
              {lang === 'es' ? 'Consentimiento firmado' : 'Signed consent'}
              <span className="font-mono text-xs text-stone-500 truncate">{preview?.name}</span>
            </DialogTitle>
          </DialogHeader>
          <iframe
            title={preview?.name || 'preview-pdf'}
            src={preview?.url}
            className="flex-1 w-full border border-stone-200 rounded-sm bg-stone-50"
            data-testid="consent-signed-preview-iframe"
          />
          <DialogFooter className="pt-2">
            <Button onClick={closePreview} variant="outline" className="rounded-sm" data-testid="consent-signed-preview-close">
              {lang === 'es' ? 'Cerrar' : 'Close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ============================================================
// URS-F268: Hoja Informativa (PDF subido a R2 + envio automatico
// 4 dias antes de T0 a voluntarios apto + envio manual).
// ============================================================
function InfoSheetSection({ studyId, lang }) {
  // URS-F359: misma consulta per-estudio que el panel padre.
  const [isPureDirector, setIsPureDirector] = useState(false);
  useEffect(() => {
    if (!studyId) return;
    axios.get(`${API_URL}/api/studies/${studyId}/my-pii-access`, { withCredentials: true })
      .then(r => setIsPureDirector(!!r.data?.is_pure_director))
      .catch(() => setIsPureDirector(false));
  }, [studyId]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [aptoVolunteers, setAptoVolunteers] = useState([]);
  const [sendingTo, setSendingTo] = useState(null);
  // URS-F276: selector de status objetivo (Aptos para cosmeticos/cosmetico_plus,
  // Pendientes para sanitario/medicamento/suplemento). Ambas pestanas siempre
  // visibles -- el admin puede saltar entre ellas en cualquier estudio.
  const [targetStatus, setTargetStatus] = useState('apto');
  const [productType, setProductType] = useState(null);
  // Cache de voluntarios por status para no refetch al toggle.
  const [volByStatus, setVolByStatus] = useState({ apto: null, pendiente: null });

  // URS-F421: mapa sheet_id -> sheet (para pintar badges "Hoja adultos/tutor/menor..."
  // junto al indicador de envio en cada voluntario). Retro-compat: si un voluntario
  // solo tiene `info_sheet_sent_at` (legacy, envio pre-multi-hoja), se muestra el badge
  // "Enviado" tal cual sin badges de detalle.
  const sheetsById = React.useMemo(() => {
    const m = {};
    for (const s of (meta?.info_sheets || [])) {
      if (s?.sheet_id) m[s.sheet_id] = s;
    }
    return m;
  }, [meta]);

  // URS-F421: color del badge segun el `label` de la hoja (parseo case-insensitive).
  // Los labels los define el usuario libremente al subir la hoja; nos basamos en
  // convenciones habituales (adulto/tutor/menor). Fallback: gris neutro.
  const getSheetBadgeStyle = (label) => {
    const s = (label || '').toLowerCase();
    if (/(tutor|guardian|padre|madre|responsable)/.test(s))
      return 'bg-violet-100 text-violet-700 border-violet-300';
    if (/(menor|min[oó]r|adolescente|ni[nñ]o)/.test(s))
      return 'bg-amber-100 text-amber-700 border-amber-300';
    if (/(adulto|mayor|general|principal)/.test(s))
      return 'bg-sky-100 text-sky-700 border-sky-300';
    return 'bg-stone-100 text-stone-700 border-stone-300';
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // URS-F350: nuevo endpoint plural devuelve {info_sheets: [], stats}.
      const { data } = await axios.get(`${API_URL}/api/studies/${studyId}/info-sheets`, { withCredentials: true });
      setMeta(data);
    } catch (e) { /* noop */ }
    try {
      const { data: s } = await axios.get(`${API_URL}/api/studies/${studyId}`, { withCredentials: true });
      const pt = (s?.product_type || '').toLowerCase();
      setProductType(pt);
      const PENDIENTE_PT = new Set(['sanitario', 'medicamento', 'suplemento']);
      setTargetStatus(PENDIENTE_PT.has(pt) ? 'pendiente' : 'apto');
    } catch (e) { /* noop */ }
    setLoading(false);
  }, [studyId]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error(lang === 'es' ? 'Solo se admiten archivos PDF' : 'Only PDF files allowed');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error(lang === 'es' ? 'El archivo supera 20 MB' : 'File exceeds 20 MB');
      return;
    }
    // URS-F350: pedir etiqueta opcional (Adultos / Ninos / etc.)
    const label = window.prompt(
      lang === 'es'
        ? 'Etiqueta para esta hoja (opcional): Adultos, Ninos, Tutor, etc.'
        : 'Label for this sheet (optional): Adults, Minors, Guardian, etc.',
      '',
    );
    if (label === null) return; // cancelado
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('label', (label || '').trim());
    try {
      await axios.post(`${API_URL}/api/studies/${studyId}/info-sheets`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(lang === 'es' ? 'Hoja informativa cargada' : 'Info sheet uploaded');
      load();
    } catch (err) {
      toast.error(errText(err));
    }
    setUploading(false);
  };

  const handleDelete = async (sheetId) => {
    if (!window.confirm(lang === 'es' ? '\u00bfEliminar esta hoja?' : 'Delete this sheet?')) return;
    try {
      await axios.delete(`${API_URL}/api/studies/${studyId}/info-sheets/${sheetId}`, { withCredentials: true });
      toast.success(lang === 'es' ? 'Eliminado' : 'Deleted');
      load();
    } catch (e) {
      toast.error(errText(e, 'Error'));
    }
  };

  const handleToggleEmail = async (sheet) => {
    const newVal = !sheet.send_by_email;
    try {
      await axios.patch(
        `${API_URL}/api/studies/${studyId}/info-sheets/${sheet.sheet_id}`,
        { send_by_email: newVal },
        { withCredentials: true },
      );
      // Optimistic update
      setMeta(prev => ({
        ...prev,
        info_sheets: (prev?.info_sheets || []).map(s =>
          s.sheet_id === sheet.sheet_id ? { ...s, send_by_email: newVal } : s
        ),
      }));
    } catch (e) {
      toast.error(errText(e, 'Error'));
    }
  };

  const loadVolunteers = useCallback(async (status) => {
    try {
      const { data } = await axios.get(
        `${API_URL}/api/studies/${studyId}/volunteers?status=${status}`,
        { withCredentials: true },
      );
      const arr = Array.isArray(data) ? data : (data.items || []);
      setVolByStatus(prev => ({ ...prev, [status]: arr }));
      if (status === targetStatus) setAptoVolunteers(arr);
    } catch (e) {
      toast.error(lang === 'es' ? `Error cargando voluntarios ${status}` : `Error loading ${status} volunteers`);
    }
  }, [studyId, targetStatus, lang]);

  const openSendDialog = async () => {
    setShowSendDialog(true);
    setVolByStatus({ apto: null, pendiente: null });
    setAptoVolunteers([]);
    await loadVolunteers(targetStatus);
  };

  // Al cambiar de pestana, refrescar o reusar cache.
  useEffect(() => {
    if (!showSendDialog) return;
    const cached = volByStatus[targetStatus];
    if (cached) {
      setAptoVolunteers(cached);
    } else {
      loadVolunteers(targetStatus);
    }
  }, [targetStatus, showSendDialog, volByStatus, loadVolunteers]);

  // URS-F350: el dialogo manual permite elegir hojas. Por defecto adjunta
  // las que tengan send_by_email=true; el admin puede override aqui.
  const [manualSheetIds, setManualSheetIds] = useState([]);
  useEffect(() => {
    if (showSendDialog) {
      const def = (meta?.info_sheets || []).filter(s => s.send_by_email).map(s => s.sheet_id);
      setManualSheetIds(def);
    }
  }, [showSendDialog, meta]);

  const handleSendTo = async (volunteerId) => {
    setSendingTo(volunteerId);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/studies/${studyId}/info-sheets/send/${volunteerId}`,
        manualSheetIds.length ? { sheet_ids: manualSheetIds } : {},
        { withCredentials: true },
      );
      toast.success(`${lang === 'es' ? 'Enviado a' : 'Sent to'} ${data.to}`);
      openSendDialog();
      load();
    } catch (e) {
      toast.error(errText(e, 'Error'));
    }
    setSendingTo(null);
  };

  const sheets = meta?.info_sheets || [];
  const hasAnyActive = sheets.some(s => s.send_by_email);

  return (
    <Card className="border-amber-200 bg-amber-50/40" data-testid="info-sheet-section">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="bg-amber-100 rounded-sm p-2 text-amber-700"><FilePdf size={22} /></div>
            <div>
              <h3 className="font-semibold text-stone-900 text-sm">
                {lang === 'es' ? 'Hojas Informativas' : 'Info Sheets'}
              </h3>
              <p className="text-[12px] text-stone-600 max-w-xl">
                {lang === 'es'
                  ? 'Sube uno o varios PDFs (Adultos, Ninos, Tutor...). Cada hoja tiene un switch "Enviar por email": si esta activo, se incluye en los envios automaticos D-4/D-2 y en los manuales por defecto.'
                  : 'Upload one or more PDFs (Adults, Minors, Guardian...). Each sheet has a "Send by email" switch. When ON the sheet is included in D-4/D-2 auto-emails and manual sends by default.'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <label className="cursor-pointer">
              <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={handleUpload} data-testid="info-sheet-upload-input" />
              <Button asChild variant="outline" size="sm" disabled={uploading} className="gap-1.5" data-testid="info-sheet-upload-btn">
                <span>
                  <UploadSimple size={14} />
                  {lang === 'es' ? 'Anadir hoja' : 'Add sheet'}
                </span>
              </Button>
            </label>
            {hasAnyActive && !isPureDirector && (
              <Button size="sm" className="gap-1.5 bg-amber-600 hover:bg-amber-700 text-white"
                onClick={openSendDialog} data-testid="info-sheet-send-btn">
                <Envelope size={14} />
                {lang === 'es' ? 'Enviar manualmente' : 'Send manually'}
              </Button>
            )}
          </div>
        </div>

        {loading ? (
          <p className="text-xs text-stone-400">{lang === 'es' ? 'Cargando...' : 'Loading...'}</p>
        ) : sheets.length === 0 ? (
          <p className="text-xs text-amber-700 bg-amber-100/50 border border-amber-200 rounded-sm px-3 py-2">
            {lang === 'es' ? 'Aun no hay hojas informativas. Sube al menos un PDF para activar el envio.' : 'No info sheets uploaded yet.'}
          </p>
        ) : (
          <div className="space-y-2" data-testid="info-sheets-list">
            {sheets.map(s => (
              <div key={s.sheet_id} className="flex items-center gap-3 flex-wrap text-[12px] text-stone-700 bg-white border border-amber-200 rounded-sm px-3 py-2" data-testid={`info-sheet-row-${s.sheet_id}`}>
                {s.label && s.label !== 'Hoja informativa' && (
                  <Badge variant="outline" className="text-[10px] bg-stone-100 border-stone-300">{s.label}</Badge>
                )}
                <span className="font-mono truncate max-w-[260px]" title={s.filename}>{s.filename}</span>
                <span className="text-stone-400">·</span>
                <span>{(s.file_size / 1024).toFixed(1)} KB</span>
                <span className="text-stone-400">·</span>
                <span>{lang === 'es' ? 'Cargado' : 'Uploaded'} {(s.uploaded_at || '').slice(0, 16).replace('T', ' ')}</span>
                <div className="ml-auto flex items-center gap-2">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <Switch
                      data-testid={`info-sheet-toggle-email-${s.sheet_id}`}
                      checked={!!s.send_by_email}
                      onCheckedChange={() => handleToggleEmail(s)}
                    />
                    <span className={`text-[11px] font-medium ${s.send_by_email ? 'text-emerald-700' : 'text-stone-400'}`}>
                      {s.send_by_email
                        ? (lang === 'es' ? 'Enviar por email' : 'Send by email')
                        : (lang === 'es' ? 'No enviar' : 'Do not send')}
                    </span>
                  </label>
                  <Button variant="outline" size="sm" className="h-7 px-2"
                    onClick={() => window.open(`${API_URL}/api/studies/${studyId}/info-sheets/${s.sheet_id}/download`, '_blank')}
                    data-testid={`info-sheet-download-${s.sheet_id}`}>
                    <DownloadSimple size={13} />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-red-600 hover:bg-red-50"
                    onClick={() => handleDelete(s.sheet_id)} data-testid={`info-sheet-delete-${s.sheet_id}`}>
                    <Trash size={13} />
                  </Button>
                </div>
              </div>
            ))}
            <div className="flex items-center gap-2 mt-1 text-[11px]">
              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                {meta?.sent_count || 0} {lang === 'es' ? 'enviados' : 'sent'}
              </Badge>
              <Badge variant="outline" className="border-amber-300 text-amber-700">
                {meta?.pending_count || 0} {lang === 'es' ? 'pendientes' : 'pending'}
              </Badge>
              <Badge variant="outline">{meta?.apto_count || 0} apto</Badge>
              {!hasAnyActive && (
                <Badge variant="outline" className="border-red-300 text-red-700 bg-red-50">
                  {lang === 'es' ? 'Ninguna hoja activa para email' : 'No sheet active for email'}
                </Badge>
              )}
            </div>
          </div>
        )}
      </CardContent>

      <Dialog open={showSendDialog} onOpenChange={(v) => !v && setShowSendDialog(false)}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col" data-testid="info-sheet-send-dialog">
          <DialogHeader>
            <DialogTitle>{lang === 'es' ? 'Enviar Hojas Informativas' : 'Send Info Sheets'}</DialogTitle>
            <DialogDescription>
              {lang === 'es'
                ? 'Selecciona el voluntario al que enviar el email. Por defecto se adjuntan las hojas con "Enviar por email" activo; puedes incluir tambien las inactivas marcandolas abajo.'
                : 'Pick the volunteer. By default only sheets with "Send by email" ON are attached; you can include inactive ones via the checkboxes below.'}
            </DialogDescription>
          </DialogHeader>
          {/* URS-F369: contenedor scrollable para que el dialogo se quede
              dentro de la pantalla aunque haya muchas hojas + voluntarios.
              El footer con "Cerrar" queda siempre visible. */}
          <div className="flex-1 min-h-0 overflow-y-auto pr-1 space-y-3">
          {/* URS-F350: selector de hojas a adjuntar en el envio manual. */}
          {sheets.length > 0 && (
            <div className="border border-amber-200 bg-amber-50/40 rounded-sm p-2 space-y-1" data-testid="info-sheet-manual-sheet-picker">
              <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">
                {lang === 'es' ? 'Hojas a adjuntar' : 'Sheets to attach'}
              </div>
              {sheets.map(s => (
                <label key={s.sheet_id} className="flex items-center gap-2 cursor-pointer text-xs">
                  <input
                    type="checkbox"
                    data-testid={`info-sheet-manual-pick-${s.sheet_id}`}
                    checked={manualSheetIds.includes(s.sheet_id)}
                    onChange={(e) => setManualSheetIds(prev =>
                      e.target.checked ? [...prev, s.sheet_id] : prev.filter(x => x !== s.sheet_id)
                    )}
                  />
                  <span className="font-mono">{s.filename}</span>
                  {s.label && s.label !== 'Hoja informativa' && (
                    <Badge variant="outline" className="text-[9px] bg-stone-100">{s.label}</Badge>
                  )}
                  {!s.send_by_email && (
                    <Badge variant="outline" className="text-[9px] border-red-300 text-red-600 bg-red-50">
                      {lang === 'es' ? 'Toggle OFF' : 'Toggle OFF'}
                    </Badge>
                  )}
                </label>
              ))}
            </div>
          )}
          {/* URS-F276: tabs Aptos / Pendientes. Default selection driven by
              product_type, but both tabs are always available so the admin
              can override manually. */}
          <div className="flex items-center gap-2 border-b border-stone-200 pb-2 mb-2" data-testid="info-sheet-status-tabs">
            {[
              { key: 'apto', es: 'Aptos', en: 'Apto' },
              { key: 'pendiente', es: 'Pendientes', en: 'Pending' },
            ].map(t => {
              const active = targetStatus === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  data-testid={`info-sheet-status-tab-${t.key}`}
                  onClick={() => setTargetStatus(t.key)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-colors ${
                    active
                      ? 'bg-[#1f3e80] text-white'
                      : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                  }`}
                >
                  {lang === 'es' ? t.es : t.en}
                </button>
              );
            })}
            <span className="ml-auto text-[11px] text-stone-500 italic" data-testid="info-sheet-auto-hint">
              {(() => {
                const PENDIENTE_PT = new Set(['sanitario', 'medicamento', 'suplemento']);
                const autoPhase = PENDIENTE_PT.has(productType) ? 'Valoracion' : 'T0';
                return lang === 'es'
                  ? `Auto: 4 dias antes de la cita ${autoPhase}`
                  : `Auto: 4 days before the ${autoPhase === 'Valoracion' ? 'Screening' : 'T0'} visit`;
              })()}
            </span>
          </div>
          <div className="space-y-2">
            {aptoVolunteers.length === 0 ? (
              <p className="text-sm text-stone-500 text-center py-6" data-testid="info-sheet-empty">
                {lang === 'es'
                  ? `No hay voluntarios en estado ${targetStatus === 'apto' ? 'Apto' : 'Pendiente'}.`
                  : `No volunteers in ${targetStatus === 'apto' ? 'Apto' : 'Pending'}.`}
              </p>
            ) : aptoVolunteers.map(v => {
              const lg = v.legal_guardian || {};
              const volEmail = v.volunteer_email || '';
              const sentAt = v.info_sheet_sent_at;
              const target = lg.enabled ? (lg.email || volEmail) : volEmail;
              const guardianName = lg.enabled ? `${(lg.nombre || '').trim()} ${(lg.apellidos || '').trim()}`.trim() : '';
              return (
                <div key={v.volunteer_id} className="flex items-center gap-3 bg-white border border-stone-200 rounded-sm px-3 py-2" data-testid={`info-sheet-vol-row-${v.volunteer_id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-stone-900 truncate">
                      {v.volunteer_name || v.volunteer_id}
                      {typeof v.volunteer_age === 'number' && (
                        <span className="text-stone-500 text-[11px] ml-2" data-testid={`info-sheet-vol-age-${v.volunteer_id}`}>
                          · {v.volunteer_age} {lang === 'es' ? 'años' : 'y'}
                        </span>
                      )}
                      {v.study_volunteer_code && <span className="text-stone-400 font-mono text-[11px] ml-2">{v.study_volunteer_code}</span>}
                    </p>
                    <p className="text-[11px] text-stone-500 truncate">
                      {target || (lang === 'es' ? 'Sin email' : 'No email')}
                      {lg.enabled && <span className="ml-1.5 text-violet-600">· {lang === 'es' ? 'Tutor' : 'Guardian'}{guardianName ? `: ${guardianName}` : ''}</span>}
                    </p>
                  </div>
                  {sentAt ? (
                    <div className="flex items-center gap-1.5 flex-wrap justify-end">
                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">
                        {lang === 'es' ? 'Enviado' : 'Sent'} {new Date(sentAt).toLocaleDateString('es-ES')}
                      </Badge>
                      {/* URS-F421: badges por hoja enviada. Solo aparecen si el
                          estudio tiene mas de una hoja definida (multi-hoja) O si
                          el voluntario tiene el detalle guardado. Retro-compat:
                          si no hay sheet_ids (envio legacy), no se muestra nada
                          adicional aparte del badge verde de arriba. */}
                      {(meta?.info_sheets?.length > 1) && Array.isArray(v.info_sheet_sent_sheet_ids) &&
                        v.info_sheet_sent_sheet_ids.map(sid => {
                          const sh = sheetsById[sid];
                          if (!sh) return null;
                          return (
                            <Badge
                              key={sid}
                              variant="outline"
                              className={`text-[10px] ${getSheetBadgeStyle(sh.label)}`}
                              title={sh.filename || sh.label || ''}
                              data-testid={`info-sheet-vol-row-${v.volunteer_id}-badge-${sid}`}
                            >
                              {sh.label || (lang === 'es' ? 'Hoja' : 'Sheet')}
                            </Badge>
                          );
                        })}
                    </div>
                  ) : (
                    <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700">
                      {lang === 'es' ? 'Pendiente' : 'Pending'}
                    </Badge>
                  )}
                  <Button
                    size="sm"
                    variant={sentAt ? 'outline' : 'default'}
                    disabled={!target || sendingTo === v.volunteer_id}
                    className="gap-1.5 shrink-0"
                    onClick={() => handleSendTo(v.volunteer_id)}
                    data-testid={`info-sheet-send-to-${v.volunteer_id}`}
                    title={!target ? (lang === 'es' ? 'Sin direcci\u00f3n de email v\u00e1lida' : 'No valid email') : ''}
                  >
                    <Envelope size={13} />
                    {sentAt ? (lang === 'es' ? 'Reenviar' : 'Resend') : (lang === 'es' ? 'Enviar' : 'Send')}
                  </Button>
                </div>
              );
            })}
          </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendDialog(false)}>{lang === 'es' ? 'Cerrar' : 'Close'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


// ============================================================
// URS-F414: Otros Documentos (PDFs libres asociados al estudio)
// - Sube uno o varios PDFs. Sin logica de email ni de version.
// - Listado con nombre, tamaño, ver (inline) y eliminar.
// ============================================================
function OtherDocumentsSection({ studyId, lang }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/studies/${studyId}/other-documents`,
        { withCredentials: true },
      );
      setDocs(data?.other_documents || []);
    } catch {
      /* noop */
    }
    setLoading(false);
  }, [studyId]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    // Etiqueta comun opcional para todo el batch. Se puede renombrar cada
    // documento por separado despues.
    const label = window.prompt(
      lang === 'es'
        ? `Etiqueta para ${files.length > 1 ? 'estos documentos' : 'este documento'} (opcional): ej. Protocolo v1, Addenda, Checklist...`
        : `Label for ${files.length > 1 ? 'these documents' : 'this document'} (optional): e.g. Protocol v1, Addenda, Checklist...`,
      '',
    );
    if (label === null) return; // cancelado
    const trimmedLabel = (label || '').trim();
    let uploadedCount = 0;
    let failedCount = 0;
    for (const file of files) {
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        toast.error(lang === 'es' ? `Solo PDF: ${file.name}` : `PDF only: ${file.name}`);
        failedCount++;
        continue;
      }
      if (file.size > 20 * 1024 * 1024) {
        toast.error(lang === 'es' ? `Supera 20 MB: ${file.name}` : `Exceeds 20 MB: ${file.name}`);
        failedCount++;
        continue;
      }
      setUploading(true);
      const fd = new FormData();
      fd.append('file', file);
      if (trimmedLabel) fd.append('label', trimmedLabel);
      try {
        await axios.post(`${API_URL}/api/studies/${studyId}/other-documents`, fd, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        uploadedCount++;
      } catch (err) {
        failedCount++;
        toast.error(errText(err, lang === 'es' ? `Error subiendo ${file.name}` : `Error uploading ${file.name}`));
      }
      setUploading(false);
    }
    if (uploadedCount > 0) {
      toast.success(
        lang === 'es'
          ? (uploadedCount === 1 ? 'Documento adjuntado' : `${uploadedCount} documentos adjuntados`)
          : (uploadedCount === 1 ? 'Document attached' : `${uploadedCount} documents attached`)
      );
    } else if (failedCount > 0) {
      toast.error(
        lang === 'es'
          ? 'No se pudo adjuntar ningun documento'
          : 'No document could be attached'
      );
    }
    load();
  };

  const handleRename = async (doc) => {
    const next = window.prompt(
      lang === 'es' ? 'Nueva etiqueta (vacio para quitar):' : 'New label (empty to clear):',
      doc.label || '',
    );
    if (next === null) return;
    try {
      await axios.patch(
        `${API_URL}/api/studies/${studyId}/other-documents/${doc.doc_id}`,
        { label: next.trim() },
        { withCredentials: true },
      );
      toast.success(lang === 'es' ? 'Etiqueta actualizada' : 'Label updated');
      load();
    } catch (e) {
      toast.error(errText(e, 'Error'));
    }
  };

  const handleDelete = async (doc) => {
    const msg = lang === 'es'
      ? `Eliminar "${doc.filename}"?`
      : `Delete "${doc.filename}"?`;
    if (!window.confirm(msg)) return;
    try {
      await axios.delete(
        `${API_URL}/api/studies/${studyId}/other-documents/${doc.doc_id}`,
        { withCredentials: true },
      );
      toast.success(lang === 'es' ? 'Documento eliminado' : 'Document deleted');
      load();
    } catch (e) {
      toast.error(errText(e, 'Error'));
    }
  };

  const handleView = async (doc) => {
    try {
      const resp = await axios.get(
        `${API_URL}/api/studies/${studyId}/other-documents/${doc.doc_id}/download`,
        { withCredentials: true, responseType: 'blob' },
      );
      const blob = resp.data.type === 'application/pdf'
        ? resp.data
        : new Blob([resp.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      // No revoke inmediato: el nuevo tab necesita el blob.
    } catch {
      toast.error(lang === 'es' ? 'Error abriendo PDF' : 'PDF open error');
    }
  };

  return (
    <Card className="border-stone-200" data-testid="other-documents-section">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="bg-stone-100 rounded-sm p-2 text-stone-700"><FilePdf size={22} /></div>
            <div>
              <h3 className="font-semibold text-stone-900 text-sm">
                {lang === 'es' ? 'Otros Documentos' : 'Other Documents'}
              </h3>
              <p className="text-[12px] text-stone-600 max-w-xl">
                {lang === 'es'
                  ? 'Adjunta uno o varios PDF al estudio (protocolo, addenda, checklist, etc.). No se envian por email.'
                  : 'Attach one or more PDFs to the study (protocol, addenda, checklist, etc.). Not emailed.'}
              </p>
            </div>
          </div>
          <label className="cursor-pointer">
            <input
              type="file"
              accept="application/pdf,.pdf"
              multiple
              className="hidden"
              onChange={handleUpload}
              data-testid="other-doc-upload-input"
            />
            <Button asChild variant="outline" size="sm" disabled={uploading} className="gap-1.5" data-testid="other-doc-upload-btn">
              <span>
                <UploadSimple size={14} />
                {uploading
                  ? (lang === 'es' ? 'Subiendo...' : 'Uploading...')
                  : (lang === 'es' ? 'Adjuntar PDF' : 'Attach PDF')}
              </span>
            </Button>
          </label>
        </div>

        {loading ? (
          <p className="text-xs text-stone-400">{lang === 'es' ? 'Cargando...' : 'Loading...'}</p>
        ) : docs.length === 0 ? (
          <p className="text-xs text-stone-500 bg-stone-50 border border-stone-200 rounded-sm px-3 py-2" data-testid="other-doc-empty">
            {lang === 'es' ? 'Aun no hay documentos adjuntos.' : 'No documents attached yet.'}
          </p>
        ) : (
          <div className="space-y-2" data-testid="other-doc-list">
            {docs.map(d => (
              <div key={d.doc_id} className="flex items-center gap-3 flex-wrap text-[12px] text-stone-700 bg-white border border-stone-200 rounded-sm px-3 py-2" data-testid={`other-doc-row-${d.doc_id}`}>
                <FilePdf size={16} className="text-rose-600 shrink-0" />
                {d.label ? (
                  <Badge
                    variant="outline"
                    className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200"
                    data-testid={`other-doc-label-${d.doc_id}`}
                  >
                    {d.label}
                  </Badge>
                ) : null}
                <span className="font-mono truncate flex-1 min-w-0" title={d.filename} data-testid={`other-doc-name-${d.doc_id}`}>
                  {d.filename}
                </span>
                <span className="text-stone-400">·</span>
                <span className="text-stone-500 whitespace-nowrap">{(d.file_size / 1024).toFixed(1)} KB</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRename(d)}
                  className="h-7 gap-1 text-stone-600 hover:text-stone-900"
                  data-testid={`other-doc-rename-${d.doc_id}`}
                  title={lang === 'es' ? 'Editar etiqueta' : 'Edit label'}
                >
                  <Pencil size={12} />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleView(d)}
                  className="h-7 gap-1 text-[#1f3e80] hover:text-[#2a4f8a]"
                  data-testid={`other-doc-view-${d.doc_id}`}
                >
                  <Eye size={12} />
                  {lang === 'es' ? 'Ver' : 'View'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(d)}
                  className="h-7 text-red-600 hover:text-red-700 hover:bg-red-50"
                  data-testid={`other-doc-delete-${d.doc_id}`}
                >
                  <Trash size={12} />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
